<template>
    <CardWithImage
        :description="getDescription(selected)"
        :is-start-price="selected.__typename !== 'CatalogueWeddingRing'"
        :lab-grown
        :price-diamond-note
        :supports-express="item.supportsExpress"
        :link-aria-label="getProductLinkAriaLabel(selected)"
        :name="getName(selected)"
        :total-sale-price="displayFromPrice"
        :url="productRoute"
        :sizes="sizes"
        :image="getImage(selected, imageLoading, imageFetchPriority, sizes)"
        class="product-card"
        data-qa="from-price-includes-default-stone"
        @link-clicked="$emit('linkClicked', selected)"
    >
        <template #rating>
            <div
                v-if="item.reviewsAggregate.total"
                class="rating"
            >
                {{ item.reviewsAggregate.rating }} ({{
                    item.reviewsAggregate.total
                }})
            </div>
        </template>

        <template #metal-variation>
            <MetalVariations
                v-if="variations"
                class="metal-variation"
                :selected
                :variations
                :link="false"
                :show-title="false"
                @click="setSelected"
            />
        </template>

        <template #save-for-later>
            <!-- <SaveForLaterButton
                :product="selected"
                :page-type
            /> -->
        </template>
    </CardWithImage>
</template>

<script lang="ts" setup generic="V extends ProductCardFragment">
import { computed, ref, type Ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { type MoneyFragment, type ProductCardFragment } from '@/graphql/api'
import { isSuffixLabGrown } from '@/domain/product/isSuffixLabGrown.ts'
import { getStoneWeight } from '@/domain/product/ring/ring-builder'
import { getTotalSaleAmount } from '@/domain/stone/price.ts'
import { useRingBuilder } from '@/plugins/ring-builder.ts'
import {
    addMoneyAmounts,
    getPrototypeDefaultStonePriceGbp,
} from '@/domain/product/ring/unifiedPdpLabGrownPricing.ts'
import { useRoutes } from '@/router/routes.ts'
import { type ImageProps } from '@/components/Image.vue'
import {
    type SaveabledProduct,
    type SaveForLaterPageType,
} from '@/components/saved-products/useSavedProducts'

import CardWithImage from '@/components/layout/cards/CardWithImage.vue'
import MetalVariations from '@/components/rings/MetalVariations.vue'
// import SaveForLaterButton from '@/components/saved-products/SaveForLaterButton.vue'
import type { ImageSizes } from '@/components/getImageProps.ts'

const {
    item,
    sizes = undefined,
    imageLoading = undefined,
    imageFetchPriority = undefined,
} = defineProps<{
    item: V
    variations: V[] | undefined
    sizes?: ImageSizes
    imageLoading?: ImageProps['loading']
    imageFetchPriority?: ImageProps['fetchpriority']
    pageType: SaveForLaterPageType
}>()

defineEmits<{
    linkClicked: [item: V]
}>()

const labGrown = computed(() => isSuffixLabGrown(item))

const selected: Ref<V> = ref(item) as Ref<V>

watch(
    () => item,
    () => {
        selected.value = item
    }
)

const setSelected = (item: V) => {
    selected.value = item
}

const { t } = useI18n()
const { stone: ringBuilderStone } = useRingBuilder()

type EngagementRingCard = Extract<
    ProductCardFragment,
    { __typename: 'CatalogueEngagementRing' }
>

type MountPosition = NonNullable<
    NonNullable<
        NonNullable<EngagementRingCard['ringBuilderDefault']>['ringBuilder']
    >['position1']
>

const getMountPrice = (position: MountPosition | null): MoneyFragment | null =>
    position?.__typename === 'RingBuilderMountFragment'
        ? position.totalSalePrice
        : null

/** Mount price from the ring builder default, falling back to the catalogue price. */
const getEngagementRingMountPrice = (
    product: EngagementRingCard
): MoneyFragment => {
    const ringBuilder = product.ringBuilderDefault?.ringBuilder

    return (
        getMountPrice(ringBuilder?.position1 ?? null) ??
        getMountPrice(ringBuilder?.position2 ?? null) ??
        product.salePrice.total
    )
}

/**
 * Prototype PLP "From" price = ring builder default mount + the ring builder's
 * stone, or the default lab-grown stone by centre shape when the ring builder
 * has none. Backend filter/sort still use mount-only prices.
 */
const displayFromPrice = computed((): MoneyFragment => {
    const product = selected.value

    if (product.__typename !== 'CatalogueEngagementRing') {
        return product.salePrice.total
    }

    const mountPrice = getEngagementRingMountPrice(product)

    if (ringBuilderStone.value) {
        return addMoneyAmounts(
            mountPrice,
            getTotalSaleAmount(ringBuilderStone.value)
        )
    }

    const shapeCode =
        product.centreStoneConstraints.shape?.systemCode ?? 'ROUND'
    const stoneAmount = getPrototypeDefaultStonePriceGbp(shapeCode).toFixed(2)

    return addMoneyAmounts(mountPrice, {
        __typename: 'Money',
        amount: stoneAmount,
        currency: mountPrice.currency,
    })
})

const priceDiamondNote = computed((): string | null => {
    if (selected.value.__typename !== 'CatalogueEngagementRing') {
        return null
    }

    if (ringBuilderStone.value) {
        return t('catalogue.from_price_your_diamond', {
            weight: getStoneWeight(ringBuilderStone.value),
        })
    }

    return t('catalogue.from_price_default_diamond')
})

const routes = useRoutes()

/** Engagement rings keep a specific natural diamond from the ring builder when its shape fits */
const productRoute = computed(
    (): ReturnType<
        typeof routes.ring.product | typeof routes.ringBuilder.engagementRing
    > => {
        const product = selected.value
        const stone = ringBuilderStone.value

        if (
            product.__typename === 'CatalogueEngagementRing' &&
            stone?.__typename === 'GemsApi_Stone' &&
            String(stone.shapeWithInfo.code) ===
                product.centreStoneConstraints.shape?.systemCode
        ) {
            return routes.ringBuilder.engagementRing(product, stone)
        }

        return routes.ring.product(product)
    }
)
</script>

<script lang="ts">
import { assertNever } from '@/helpers'
import { getImageProps } from '@/components/getImageProps.ts'

const getImage = (
    item: ProductCardFragment,
    loading: ImageProps['loading'],
    fetchpriority: ImageProps['fetchpriority'],
    sizes: ImageSizes | undefined
): ImageProps[] | undefined =>
    item.images.map((image) => {
        return {
            ...getImageProps(image, sizes),
            alt: item.productName,
            loading,
            fetchpriority,
        }
    })

export const getName = (item: SaveabledProduct): string => {
    switch (item.__typename) {
        case 'CatalogueEngagementRing':
        // fallthrough
        case 'CatalogueWeddingRing':
        // fallthrough
        case 'CatalogueJewelleryBracelet':
        // fallthrough
        case 'CatalogueJewelleryEarrings':
        // fallthrough
        case 'CatalogueJewelleryRing':
        // fallthrough
        case 'CatalogueJewelleryNecklace':
            return item.productName
        case 'CatalogueReadyToGoRing':
            return item.shortName
        default:
            return assertNever(item, '')
    }
}

export const getProductLinkAriaLabel = (item: ProductCardFragment): string => {
    const parts = [getName(item), item.primaryMetal.translation.name]

    if (item.__typename === 'CatalogueEngagementRing') {
        // TODO: Implement, but there is an error in the graphql response
        // Cannot return null for non-nullable field StoneShape.translation
        // const shapeName = item.centreStoneConstraints.shape?.translation.name
        const shapeName =
            item.centreStoneConstraints.shape?.systemCode.toLocaleLowerCase()

        if (shapeName) {
            parts.push(shapeName)
        }
    }

    if (item.suffix) {
        parts.push(item.suffix)
    }

    return parts.filter(Boolean).join(' ')
}

export const getDescription = (item: SaveabledProduct): string | null => {
    switch (item.__typename) {
        case 'CatalogueEngagementRing':
        // fallthrough
        case 'CatalogueWeddingRing':
        // fallthrough
        case 'CatalogueJewelleryBracelet':
        // fallthrough
        case 'CatalogueJewelleryEarrings':
        // fallthrough
        case 'CatalogueJewelleryRing':
        // fallthrough
        case 'CatalogueJewelleryNecklace':
            return item.translation.description
        case 'CatalogueReadyToGoRing':
            return item.shortDescription
        default:
            return assertNever(item, '')
    }
}
</script>

<style lang="scss" scoped>
.rating {
    position: relative;
    font-family: $effra-font-stack;
    font-size: 14px;
    color: $slate;

    &::before {
        position: absolute;
        content: '';
        width: 12px;
        height: 12px;
        top: 3px;
        left: -14px;
        background: url('@/assets/icons/star-filled-slate.svg');
        background-size: contain;
        background-repeat: no-repeat;
    }
}
</style>
